import type { GalleryItem } from "@/contexts/GalleryContext";
import awareIcon from "@/assets/icons/aware.webp";
import enpalIcon from "@/assets/icons/enpal.webp";
import skipIcon from "@/assets/icons/skip.webp";
import vodafoneIcon from "@/assets/icons/vodafone.webp";
import buergerstiftungIcon from "@/assets/icons/buergerstiftung.webp";
import nurseryIcon from "@/assets/icons/nursery.webp";
import umIcon from "@/assets/icons/um.webp";
import smuIcon from "@/assets/icons/smu.webp";
import stclaresIcon from "@/assets/icons/stclares.webp";
import comeniusIcon from "@/assets/icons/comenius.webp";
import smuCampus from "@/assets/cards/smu-campus.webp";
import maastrichtCampus from "@/assets/cards/maastricht-campus.webp";
import awareHealthBg from "@/assets/cards/aware-health.webp";
import enpalBg from "@/assets/cards/enpal.webp";
import skipBg from "@/assets/cards/skip-app.webp";
import vodafoneBg from "@/assets/cards/vodafone-campus.webp";
import buergerstiftungBg from "@/assets/cards/buergerstiftung.webp";
import comeniusBg from "@/assets/cards/comenius-gymnasium.webp";
import stclaresBg from "@/assets/cards/stclares-oxford.webp";
import nurseryBg from "@/assets/cards/nursery-school.webp";

type DetailBase = Omit<GalleryItem, "addedAt">;

export const activityDetails: Record<string, DetailBase> = {
  "aware-health": {
    id: "aware-health",
    category: "experience",
    icon: awareIcon,
    backgroundImage: awareHealthBg,
    title: "Aware Health GmbH",
    subtitle: "Design & Product · Product, Growth & Marketing",
    roles: [
      { title: "Working Student", subtitle: "Design & Product", duration: "Sep 2025 – Dec 2025" },
      { title: "Intern", subtitle: "Product, Growth & Marketing", duration: "Jun 2025 – Aug 2025" },
    ],
    startDate: "Jun 2025",
    endDate: "Dec 2025",
    overview: [
      { label: "Duration", value: "Jun 2025 – Dec 2025" },
      { label: "Focus", value: "Product Design, Growth Strategy, User Research" },
      { label: "Industry", value: "Digital & Preventive Health" },
    ],
    summary:
      "Aware is a digital and preventive health company that offers holistic blood tests through its app, delivering certified lab results within 48 hours. Contributed across product design, growth strategy, and marketing to help users stay on top of their health.",
    contributions: [
      "— Design (Figma) —",
      "Landing pages: DM x Aware, ESN x Aware, one page for each Aware test",
      "Designed partnership flyers and business cards for affiliate partners",
      "Redesigned homepage for conversion: clearer CTAs, trust signals, simplified structure",
      "— Growth & Partnerships —",
      "Aware Dashboard: a web platform for uploading, analysing and acting on blood results from any lab (Lovable)",
      "Built interactive map for Aware Lab locations (Lovable)",
      "Built interactive quiz recommender for DM pilot launch (Lovable)",
      "Built and managed affiliate partnership programme",
      "— Marketing —",
      "Translated and SEO optimized 31 magazine articles (Webflow)",
      "Managed Instagram account: content design, scheduling and posting",
      "Tested and implemented AI tools for video dubbing and Instagram Reel repurposing",
    ],
    learnings: [
      "How to balance user needs with business objectives in health-tech",
      "Running lean experiments to validate product hypotheses quickly",
      "Cross-functional collaboration between design, engineering, and clinical teams",
    ],
  },
  "enpal": {
    id: "enpal",
    category: "experience",
    icon: enpalIcon,
    backgroundImage: enpalBg,
    title: "Enpal B.V",
    subtitle: "SEO & Business Development Intern",
    startDate: "May 2023",
    endDate: "Jul 2023",
    overview: [
      { label: "Duration", value: "May 2023 – Jul 2023" },
      { label: "Focus", value: "SEO Strategy, Business Development" },
      { label: "Industry", value: "Renewable Energy / Solar" },
    ],
    summary:
      "Europe's leading solar energy company. Supported SEO strategy and business development initiatives to drive organic growth across multiple markets.",
    contributions: [
      "Created SEO optimized articles and implemented them on the website",
      "Supported the social media team with creation and upload of ad campaigns",
      "Independently led creative and design work",
      "Assisted with Salesforce CRM, lead processing, and data management",
    ],
    learnings: [
      "Technical SEO and how search engines evaluate content authority",
      "Scaling business development processes in a hyper-growth environment",
      "Data-driven decision making with large-scale analytics",
    ],
  },
  "skip-app": {
    id: "skip-app",
    category: "experience",
    icon: skipIcon,
    backgroundImage: skipBg,
    backgroundPosition: "top",
    title: "Skip App GmbH",
    subtitle: "Web Development & Branding Intern",
    startDate: "May 2022",
    endDate: "Jul 2022",
    overview: [
      { label: "Duration", value: "May 2022 – Jul 2022" },
      { label: "Focus", value: "Web Development, Branding" },
      { label: "Stage", value: "Early-stage Startup" },
    ],
    summary:
      "Early-stage startup building an instant photo-sharing app that simplified distributing photos to friends and reimagined how people capture moments.",
    contributions: [
      "Created brand guidelines and a cohesive visual language for the product",
      "Designed social media templates and marketing collateral",
    ],
    learnings: [
      "Shipping fast with limited resources in an early-stage startup",
      "How brand identity shapes user perception and trust",
    ],
  },
  "vodafone": {
    id: "vodafone",
    category: "experience",
    icon: vodafoneIcon,
    backgroundImage: vodafoneBg,
    backgroundPosition: "center 20%",
    title: "Vodafone GmbH",
    subtitle: "Marketing & Media Intern",
    startDate: "Jun 2021",
    endDate: "Jun 2021",
    overview: [
      { label: "Duration", value: "Jun 2021" },
      { label: "Type", value: "School Summer Internship" },
      { label: "Focus", value: "Marketing, Media Planning" },
      { label: "Industry", value: "Telecommunications" },
    ],
    summary:
      "Global telecommunications leader. Gained hands-on experience in corporate marketing, media planning, and campaign execution.",
    contributions: [
      "Assisted in planning and executing multi-channel marketing campaigns",
      "Led an LGBTQ+ awareness campaign and built the accompanying pitch presentation",
    ],
    learnings: [
      "How large corporations structure and execute marketing at scale",
      "Media planning fundamentals and budget allocation strategies",
    ],
  },
  "buergerstiftung": {
    id: "buergerstiftung",
    category: "volunteering",
    icon: buergerstiftungIcon,
    backgroundImage: buergerstiftungBg,
    backgroundPosition: "20% center",
    title: "Bürgerstiftung Düsseldorf",
    subtitle: "Sales Representative",
    startDate: "Juni 2024",
    endDate: "Dez 2025",
    overview: [
      { label: "Duration", value: "Juni 2024 – Dez 2025" },
      { label: "Role", value: "Sales Representative" },
      { label: "Location", value: "Düsseldorf" },
    ],
    summary:
      "Community foundation supporting social, cultural, and educational projects in Düsseldorf. Volunteering as a sales representative at selected social and fundraising events.",
    contributions: [],
    learnings: [
      "The power of community-driven impact and local engagement",
      "Persuasion and sales techniques in a purpose-driven context",
    ],
  },
  "nursery": {
    id: "nursery",
    category: "volunteering",
    icon: nurseryIcon,
    backgroundImage: nurseryBg,
    backgroundPosition: "center center",
    title: "Nursery School",
    subtitle: "Assistant",
    startDate: "Aug 2019",
    endDate: "Aug 2019",
    overview: [
      { label: "Duration", value: "Aug 2019" },
      { label: "Role", value: "Teaching Assistant" },
      { label: "Focus", value: "Early Childhood Education" },
    ],
    summary:
      "Volunteered as an assistant at a local nursery, supporting early childhood education and care.",
    contributions: [],
    learnings: [
      "Patience, empathy, and adaptability in working with young children",
      "The importance of early education in personal development",
    ],
  },
  "maastricht": {
    id: "maastricht",
    category: "education",
    icon: umIcon,
    backgroundImage: maastrichtCampus,
    title: "Maastricht University",
    subtitle: "B.Sc. International Business",
    startDate: "Aug 2023",
    endDate: "Aug 2026",
    overview: [
      { label: "Duration", value: "Aug 2023 – Aug 2026" },
      { label: "Degree", value: "B.Sc. International Business" },
      { label: "Major", value: "Entrepreneurship" },
      { label: "GPA", value: "8.1 (Cum Laude)" },
    ],
    summary:
      "Problem-Based Learning university ranked among Europe's top business schools. Studying international business with focuses on strategy, entrepreneurship, and digital innovation.",
    contributions: [],
    learnings: [
      "Problem-Based Learning: self-directed study and critical thinking",
      "International perspective on business strategy and operations",
      "Extensive group work, presentations, and academic paper writing",
    ],
  },
  "smu": {
    id: "smu",
    category: "education",
    icon: smuIcon,
    backgroundImage: smuCampus,
    title: "Exchange Semester at SMU",
    subtitle: "Exchange Semester",
    startDate: "Jan 2026",
    endDate: "May 2026",
    overview: [
      { label: "Duration", value: "Jan 2026 – May 2026" },
      { label: "Program", value: "Exchange Semester" },
      { label: "Location", value: "Singapore" },
      { label: "Courses", value: "AI & Humanity, Leadership & Team Building (SMU-X), Designing Communication for Behavioural Change, World Politics, Big Questions (Wealth & Poverty)" },
    ],
    summary:
      "Top Asian business university in the heart of Singapore. Exchange semester exploring AI, leadership, communication, and global politics in a highly diverse academic environment.",
    contributions: [
      "Worked closely with an industry partner (Rainmaker) on a real-world SMU-X project",
      "Travelled across Southeast Asia, immersing in diverse cultures, people, and adventures",
    ],
    learnings: [],
  },
  "stclares": {
    id: "stclares",
    category: "education",
    icon: stclaresIcon,
    backgroundImage: stclaresBg,
    backgroundPosition: "center center",
    title: "St. Clare's International College",
    subtitle: "Advanced English",
    startDate: "Sep 2022",
    endDate: "Dec 2022",
    overview: [
      { label: "Duration", value: "Sep 2022 – Dec 2022" },
      { label: "Program", value: "Advanced English & Academic Subjects" },
      { label: "Location", value: "Oxford, UK" },
      { label: "Courses", value: "English, Photography, Marketing, International Relations" },
      { label: "Achievement", value: "Cambridge Assessment – C1 Level" },
    ],
    summary:
      "Prestigious international college in Oxford, UK. Completed an advanced English and academic program, studying a diverse range of subjects alongside students from around the world.",
    contributions: [],
    learnings: [
      "Academic English writing, argumentation, and presentation skills",
      "Cross-cultural communication and collaboration in a truly international setting",
      "Self-directed learning and adaptability in a new academic environment",
    ],
  },
  "comenius": {
    id: "comenius",
    category: "education",
    icon: comeniusIcon,
    backgroundImage: comeniusBg,
    backgroundPosition: "center 80%",
    title: "Comenius Gymnasium",
    subtitle: "Abitur",
    startDate: "Aug 2014",
    endDate: "Jul 2022",
    overview: [
      { label: "Duration", value: "Aug 2014 – Jul 2022" },
      { label: "Degree", value: "Abitur" },
      { label: "A-levels", value: "Sport, English, Maths, Geography" },
      { label: "Others", value: "Captain of School Hockey Team" },
    ],
    summary:
      "German secondary school (Gymnasium) where I completed my Abitur, Germany's university entrance qualification.",
    contributions: [],
    learnings: [],
  },
};

/** Map a list-item title to its detail key */
export const titleToDetailKey: Record<string, string> = {
  "Aware Health GmbH": "aware-health",
  "Enpal B.V": "enpal",
  "Skip App GmbH": "skip-app",
  "Vodafone GmbH": "vodafone",
  "Bürgerstiftung Düsseldorf": "buergerstiftung",
  "Nursery School": "nursery",
  "Maastricht University": "maastricht",
  "Exchange Semester at SMU": "smu",
  "St. Clare's International College": "stclares",
  "Comenius Gymnasium": "comenius",
};
